import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import Arrow from "../../../Static/RawImages/arrow.png";
import FilterButton from "../../../ui/buttons/FilterButton";
import NoRecord from "../../../Components/NoRecord";
import { showToast } from "../../../utils/handleToast";
import usePagination from "../../../hooks/usePagination";
import Pagination from "../../Components/pagination/Pagination";
import InvoiceFilterModal from "../../Components/Litigation/InvoiceFilterModal";
import InvoiceDetailsModal from "../../Components/Litigation/InvoiceDetailsModal";
import { format } from "date-fns";
import {
  getClients,
  generateLitigationInvoices,
  getLitigationInvoices,
  sendLitigationInvoiceEmail,
  sendLitigationInvoiceFollowupEmail,
  updateLitigationInvoice,
} from "../../../apis/invoiceApis";
import { getInvoiceBranches } from "../../../API/litigationApis";
import useAuthStore from "../../../store/authStore";
import API from "../../../apis/axios";
import useFilterPersistence from "../../../hooks/useFilterPersistence";
import useAutoRefresh from "../../../hooks/useAutoRefresh";

const INITIAL_FILTERS = {
  client: "",
  branch_name: "",
  lan: "",
  customer_name: "",
  is_paid: "",
  invoice_number: "",
  status: "",
  invoice_date: "",
  invoice_date_from: "",
  invoice_date_to: "",
};

const getActiveFilterParams = (filters) =>
  Object.entries(filters || {}).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      if (key === "client") {
        // If client is an id, send client_id; otherwise send client_name.
        if (typeof value === "number" || /^[0-9]+$/.test(String(value))) {
          acc["client_id"] = value;
        } else {
          acc["client_name"] = value;
        }
      } else if (key === "branch") {
        // normalize legacy branch key
        acc["branch_name"] = value;
      } else if (key === "is_paid") {
        // backend expects True/False values
        if (String(value).toLowerCase() === "true") {
          acc["is_paid"] = "True";
        } else if (String(value).toLowerCase() === "false") {
          acc["is_paid"] = "False";
        }
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});

const formatInvoiceDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : format(date, "dd/MM/yyyy");
};

const formatInvoiceAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "-";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const formatPaidStatus = (value) => {
  if (value === true) return "Paid";
  if (value === false) return "Unpaid";
  return "-";
};

const formatTextValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value).trim() || "-";
};

const formatDateTimeValue = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : format(date, "dd/MM/yyyy, p");
};

const formatDateList = (value) => {
  if (!Array.isArray(value) || value.length === 0) return "-";

  return value
    .map((dateValue) => formatTextValue(dateValue))
    .filter((dateValue) => dateValue !== "-")
    .join(", ") || "-";
};

const toBranchOption = (branch) => {
  if (typeof branch === "string" || typeof branch === "number") {
    const name = String(branch).trim();
    return name ? { label: name, value: name } : null;
  }

  if (!branch || typeof branch !== "object") return null;

  const nestedBranch = branch.branch || branch.branch_obj || null;
  const branchName =
    branch.branch_name ||
    branch.name ||
    nestedBranch?.branch_name ||
    nestedBranch?.name ||
    "";

  const name = String(branchName).trim();
  return name ? { label: name, value: name } : null;
};

const ViewInvoice = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const { filters: filter, setFilters, updateFilter, clearFilters, hasActiveFilters } = useFilterPersistence("invoice", INITIAL_FILTERS);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceModalMode, setInvoiceModalMode] = useState("view");
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadClients, setUploadClients] = useState([]);
  const [uploadClientsLoading, setUploadClientsLoading] = useState(false);
  const [selectedUploadClient, setSelectedUploadClient] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [invoiceClients, setInvoiceClients] = useState([]);
  const [invoiceClientsLoading, setInvoiceClientsLoading] = useState(false);
  const [invoiceBranchOptions, setInvoiceBranchOptions] = useState([]);
  const [invoiceBranchesLoading, setInvoiceBranchesLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedClientsMap, setSelectedClientsMap] = useState({});
  const [bulkAction, setBulkAction] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkMarkPaidConfirm, setShowBulkMarkPaidConfirm] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfModalUrl, setPdfModalUrl] = useState("");
  const [pdfModalTitle, setPdfModalTitle] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
  });
  const { currentPage, handleTotal, limitPerPage, total, paginationCallBack, paginationProps, resetPagination } = usePagination({ defaultLimit: 20 });
  const selectAllRef = useRef(null);

  const visibleInvoiceIds = useMemo(
    () => (Array.isArray(data) ? data.map((item) => item?.id).filter(Boolean) : []),
    [data]
  );
  const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const allVisibleSelected = visibleInvoiceIds.length > 0 && visibleInvoiceIds.every((id) => selectedSet.has(id));
  const someVisibleSelected = visibleInvoiceIds.some((id) => selectedSet.has(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = !allVisibleSelected && someVisibleSelected;
    }
  }, [allVisibleSelected, someVisibleSelected]);

  const fetchData = useCallback(async (pageNum = 1) => {
    setLoading(true);

    try {
      const activeFilterParams = getActiveFilterParams(filter);
      const apiParams = {
        ...activeFilterParams,
      };

      console.log("Invoice API params being sent:", apiParams);

      // Always fetch only requested page; date filtering is applied on current page only.
      apiParams.page = pageNum;
      apiParams.page_size = limitPerPage;

      const res = await getLitigationInvoices(apiParams);
      const responseData = res?.data || {};
      const payload = responseData.data || responseData;
      const results = Array.isArray(payload?.results) ? payload.results : [];

      setData(results);
      handleTotal(payload?.count || 0);

      if (!res?.success) {
        showToast({ variant: "danger", message: "Failed to fetch invoices" });
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      showToast({ variant: "danger", message: "Error fetching invoices" });
    } finally {
      setLoading(false);
    }
  }, [filter, limitPerPage, handleTotal]);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, filter, fetchData]);

  const fetchInvoiceClients = useCallback(async () => {
    setInvoiceClientsLoading(true);
    try {
      const response = await getClients({ limit: 200 });
      const payload = response?.data || {};
      const clientsList = Array.isArray(payload?.data?.results)
        ? payload.data.results
        : Array.isArray(payload?.results)
          ? payload.results
          : [];

      const options = clientsList
        .map((client) => ({
          label: client?.full_name || client?.name || String(client?.id || ""),
          value: client?.id,
        }))
        .filter((option) => option.value);

      setInvoiceClients(options);
    } catch (error) {
      console.error("Error fetching invoice clients:", error);
      setInvoiceClients([]);
      showToast({ variant: "danger", message: "Failed to fetch clients" });
    } finally {
      setInvoiceClientsLoading(false);
    }
  }, []);

  const fetchInvoiceBranchOptions = useCallback(async (clientId) => {
    if (!clientId) {
      setInvoiceBranchOptions([]);
      return;
    }

    setInvoiceBranchesLoading(true);
    try {
      const res = await getInvoiceBranches({ client_id: clientId });
      const payload = res?.data || {};
      const results = Array.isArray(payload?.data?.results)
        ? payload.data.results
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

      const options = results.map(toBranchOption).filter(Boolean);
      setInvoiceBranchOptions(options);
    } catch (error) {
      console.error("Error fetching invoice branches:", error);
      setInvoiceBranchOptions([]);
      showToast({ variant: "danger", message: "Failed to fetch branches" });
    } finally {
      setInvoiceBranchesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showInvoiceModal || (invoiceModalMode !== "add" && invoiceModalMode !== "edit")) {
      return;
    }

    fetchInvoiceClients();
  }, [showInvoiceModal, invoiceModalMode, fetchInvoiceClients]);

  useEffect(() => {
    if (!showInvoiceModal || (invoiceModalMode !== "add" && invoiceModalMode !== "edit")) {
      return;
    }

    const resolvedClientId =
      editFormData?.client_id ||
      (typeof editFormData?.client === "number" || /^[0-9]+$/.test(String(editFormData?.client || ""))
        ? editFormData?.client
        : "");

    if (!resolvedClientId) {
      setInvoiceBranchOptions([]);
      return;
    }

    fetchInvoiceBranchOptions(resolvedClientId);
  }, [showInvoiceModal, invoiceModalMode, editFormData?.client_id, editFormData?.client, fetchInvoiceBranchOptions]);

  // Setup auto-refresh hook for maintaining filters after CRUD operations
  const { triggerRefetch } = useAutoRefresh(
    useCallback(() => fetchData(currentPage), [fetchData, currentPage]),
    300
  );

  const handleRefetchData = () => {
    triggerRefetch();
  };

  const handlePageChange = (page) => {
    paginationProps.handlePagination(page);
  };

  const handleToggleRowSelection = (invoice) => {
    const invoiceId = invoice?.id;
    if (!invoiceId) return;

    setSelectedRows((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId);
      }
      return [...prev, invoiceId];
    });

    setSelectedClientsMap((prev) => ({
      ...prev,
      [invoiceId]: invoice?.client_id || invoice?.client || "",
    }));
  };

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedRows((prev) => prev.filter((id) => !visibleInvoiceIds.includes(id)));
      return;
    }

    setSelectedRows((prev) => Array.from(new Set([...prev, ...visibleInvoiceIds])));
    setSelectedClientsMap((prev) => {
      const next = { ...prev };
      (data || []).forEach((item) => {
        if (item?.id) {
          next[item.id] = item?.client_id || item?.client || "";
        }
      });
      return next;
    });
  };

  const clearSelections = () => {
    setSelectedRows([]);
    setSelectedClientsMap({});
  };

  const resetBulkActionState = () => {
    setBulkAction("");
    setBulkActionLoading(false);
  };

  const executeBulkMarkAsPaid = async () => {
    if (!selectedRows.length) return;

    try {
      setBulkAction("mark_paid");
      setBulkActionLoading(true);

      const results = await Promise.allSettled(
        selectedRows.map((id) => API.get("/litigation/api/mark-invoice-as-paid/", { params: { id } }))
      );

      const successCount = results.filter(
        (result) =>
          result.status === "fulfilled" &&
          (result.value?.data?.success || result.value?.status === 200)
      ).length;

      if (successCount > 0) {
        showToast({ variant: "success", message: `${successCount} invoice(s) marked as paid` });
      }
      if (successCount !== selectedRows.length) {
        showToast({ variant: "danger", message: "Some invoices could not be marked as paid" });
      }

      setShowBulkMarkPaidConfirm(false);
      clearSelections();
      handleRefetchData();
    } catch (error) {
      console.error("Error in bulk mark as paid:", error);
      showToast({ variant: "danger", message: "Failed to mark selected invoices as paid" });
    } finally {
      resetBulkActionState();
    }
  };

  const handleBulkActionSelect = async (actionKey) => {
    if (!selectedRows.length || bulkActionLoading) return;

    if (actionKey === "mark_paid") {
      setShowBulkMarkPaidConfirm(true);
      return;
    }

    try {
      setBulkAction(actionKey);
      setBulkActionLoading(true);

      if (actionKey === "download") {
        selectedRows.forEach((id) => {
          const invoiceItem = (data || []).find((item) => item?.id === id);
          handleDownloadInvoice(invoiceItem || { id });
        });
        showToast({ variant: "success", message: "Download opened for selected invoices" });
      }

      if (actionKey === "send_email") {
        const results = await Promise.allSettled(selectedRows.map((id) => sendLitigationInvoiceEmail(id)));
        const successCount = results.filter((result) => result.status === "fulfilled" && result.value?.success).length;
        if (successCount > 0) {
          showToast({ variant: "success", message: `Email sent for ${successCount} invoice(s)` });
        }
        if (successCount !== selectedRows.length) {
          showToast({ variant: "danger", message: "Some invoice emails failed" });
        }
      }

      if (actionKey === "followup_email") {
        const results = await Promise.allSettled(selectedRows.map((id) => sendLitigationInvoiceFollowupEmail(id)));
        const successCount = results.filter((result) => result.status === "fulfilled" && result.value?.success).length;
        if (successCount > 0) {
          showToast({ variant: "success", message: `Follow-up email sent for ${successCount} invoice(s)` });
        }
        if (successCount !== selectedRows.length) {
          showToast({ variant: "danger", message: "Some follow-up emails failed" });
        }
      }

      if (actionKey === "generate") {
        const idsCsv = selectedRows.join(",");
        const selectedClientIds = Array.from(
          new Set(selectedRows.map((id) => selectedClientsMap[id]).filter(Boolean))
        );

        const response = await generateLitigationInvoices({
          id: idsCsv,
          client_id: selectedClientIds.join(","),
        });

        if (response?.success) {
          showToast({ variant: "success", message: `Generate request sent for ${selectedRows.length} invoice(s)` });
          handleRefetchData();
        } else {
          showToast({ variant: "danger", message: response?.data?.message || "Failed to generate selected invoices" });
        }
      }
    } catch (error) {
      console.error("Error executing bulk action:", error);
      showToast({ variant: "danger", message: "Bulk action failed" });
    } finally {
      resetBulkActionState();
    }
  };

  const handleApplyFilters = (appliedFilters) => {
    // Check if appliedFilters is empty (clearing filters)
    const isEmpty = !appliedFilters || Object.keys(appliedFilters).length === 0;
    
    if (isEmpty) {
      clearFilters();
    } else {
      setFilters(appliedFilters);
    }
    
    clearSelections();
    resetPagination();
    setOpenFilterModal(false);
    
    // useEffect will automatically trigger and call fetchData when filters change
  };

  const handleClearAllFilters = () => {
    clearFilters();
    clearSelections();
    resetPagination();
    // useEffect will automatically trigger and call fetchData when filters change
  };

  const handleViewInvoice = (invoice) => {
    const enriched = {
      ...(invoice || {}),
      client_name: invoice?.client_name || invoice?.client || "",
    };
    setSelectedInvoice(enriched);
    setEditFormData(enriched);
    setInvoiceModalMode("view");
    setShowInvoiceModal(true);
  };

  const handleEditInvoice = (invoice) => {
    const enriched = {
      ...(invoice || {}),
      lan_number: invoice?.lan_number || invoice?.lan || invoice?.case_lan || "",
      lan: invoice?.lan || invoice?.lan_number || invoice?.case_lan || "",
      client_id:
        invoice?.client_id ||
        (typeof invoice?.client === "number" || /^[0-9]+$/.test(String(invoice?.client || ""))
          ? invoice?.client
          : ""),
      client_name: invoice?.client_name || invoice?.client || "",
    };
    setSelectedInvoice(enriched);
    setEditFormData(enriched);
    setInvoiceModalMode("edit");
    setShowInvoiceModal(true);
  };

  const handleAddInvoice = () => {
    setSelectedInvoice(null);
    setEditFormData({ client_id: "", client_name: "", branch_name: "" });
    setInvoiceModalMode("add");
    setShowInvoiceModal(true);
  };

  const handleCloseEditModal = () => {
    setShowInvoiceModal(false);
    setInvoiceModalMode("view");
    setSelectedInvoice(null);
    setEditFormData({});
  };

  const handleEditFormChange = (name, value) => {
    if (name === "client_id") {
      const selectedClient = invoiceClients.find((option) => String(option.value) === String(value));
      setEditFormData((prev) => ({
        ...prev,
        client_id: value,
        client_name: selectedClient?.label || "",
        branch_name: "",
      }));
      fetchInvoiceBranchOptions(value);
      return;
    }

    // Log LAN field changes for debugging
    if (name === "lan" || name === "lan_number") {
      console.log("LAN field changed:", value);
      setEditFormData((prev) => ({
        ...prev,
        lan_number: value,
        lan: value,
      }));
      return;
    }

    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditFormSubmit = async () => {
    const isAddMode = invoiceModalMode === "add";

    if (!isAddMode && !editFormData?.id) {
      showToast({ variant: "warning", message: "Invoice id is missing" });
      return;
    }

    setEditLoading(true);
    try {
      // Build request object with all fields
      const submitData = {};
      Object.keys(editFormData).forEach((key) => {
        // Always include the id for edit mode
        if (key === "id") {
          submitData[key] = editFormData[key];
          return;
        }
        // Skip id for add mode (backend generates it)
        if (isAddMode && key === "id") {
          return;
        }
        // Include all other fields that have values
        if (editFormData[key] !== null && editFormData[key] !== undefined) {
          submitData[key] = editFormData[key];
        }
      });

      console.log("Submitting form data with keys:", Object.keys(submitData));
      console.log("Form data values:", submitData);
      
      submitData.lan_number = submitData.lan_number ?? submitData.lan ?? "";
      submitData.lan = submitData.lan ?? submitData.lan_number ?? "";
      console.log("LAN value being sent:", submitData.lan_number || submitData.lan);

      let res;
      if (isAddMode) {
        // Add mode - use the add endpoint
        res = await API.post("https://api.themedius.ai/litigation/api/add-litigation-invoice/", submitData);
      } else {
        // Edit mode - use the update function (which uses POST to /litigation/api/litigation-invoices/)
        res = await updateLitigationInvoice(submitData);
      }

      console.log("Backend response:", res?.data);

      if (res?.data || res?.success) {
        const invoiceIdentifier = editFormData?.invoice_number || editFormData?.id || "";
        showToast({
          variant: "success",
          message: isAddMode
            ? `Invoice ${invoiceIdentifier} added successfully`
            : `Invoice ${invoiceIdentifier} updated successfully`,
        });
        handleCloseEditModal();
        // Immediately refresh the current page to show the updated LAN field
        console.log("Triggering immediate data refresh after update");
        fetchData(currentPage);
      } else {
        showToast({
          variant: "danger",
          message: res?.data?.message || (isAddMode ? "Failed to add invoice" : "Failed to update invoice"),
        });
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
      console.error("Error response status:", error?.response?.status);
      console.error("Error response data:", error?.response?.data);
      const errorMsg = error?.response?.data?.message || error?.message || (isAddMode ? "Failed to add invoice" : "Failed to update invoice");
      showToast({ variant: "danger", message: errorMsg });
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseInvoiceDetails = () => {
    handleCloseEditModal();
  };

  const handleInvoiceModalEditRequest = () => {
    if (!selectedInvoice) {
      return;
    }

    setEditFormData(selectedInvoice);
    setInvoiceModalMode("edit");
  };

  const handleDownloadInvoice = (invoice) => {
    if (invoice?.pdf_url) {
      const downloadUrl = `https://api.themedius.ai/dashboard/api/download_media/?url=${encodeURIComponent(invoice.pdf_url)}`;
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    showToast({ variant: "warning", message: `No PDF found for invoice ${invoice?.invoice_number || invoice?.id || ""}` });
  };

  const handleViewPdfInvoice = async (invoice) => {
    if (!invoice?.pdf_url) {
      showToast({ variant: "warning", message: `No PDF found for invoice ${invoice?.invoice_number || invoice?.id || ""}` });
      return;
    }

    setPdfModalTitle(invoice?.invoice_number || invoice?.id || "");
    setPdfModalUrl("");
    setPdfLoading(true);
    setShowPdfModal(true);

    try {
      const response = await API.get("/dashboard/api/download_media/", {
        params: { url: invoice.pdf_url },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      setPdfModalUrl(objectUrl);
    } catch (error) {
      console.error("Error loading PDF blob:", error);
      showToast({ variant: "danger", message: "Failed to load PDF preview" });
      setShowPdfModal(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfModal = () => {
    if (pdfModalUrl && pdfModalUrl.startsWith("blob:")) {
      URL.revokeObjectURL(pdfModalUrl);
    }
    setPdfModalUrl("");
    setShowPdfModal(false);
  };

  const handleMarkAsPaid = async (invoice) => {
    const invoiceId = invoice?.id;
    if (!invoiceId) {
      showToast({ variant: "warning", message: "Invoice id is missing" });
      return;
    }

    try {
      const res = await API.get("/litigation/api/mark-invoice-as-paid/", {
        params: { id: invoiceId },
      });

      if (res && (res.data?.success || res.status === 200)) {
        showToast({ variant: "success", message: `Invoice ${invoice?.invoice_number || invoiceId} marked as paid` });
        handleRefetchData();
        return;
      }

      showToast({ variant: "danger", message: res?.data?.message || "Failed to mark invoice as paid" });
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      showToast({ variant: "danger", message: error?.response?.data?.message || error?.message || "Error marking invoice as paid" });
    }
  };

  


  const handleSendInvoiceMail = (invoice) => {
    const invoiceId = invoice?.id;

    if (!invoiceId) {
      showToast({ variant: "warning", message: "Invoice id is missing" });
      return;
    }

    sendLitigationInvoiceEmail(invoiceId)
      .then((res) => {
        if (res?.success) {
          showToast({ variant: "success", message: `Invoice ${invoice?.invoice_number || invoiceId} email sent successfully` });
          return;
        }

        showToast({ variant: "danger", message: res?.data?.message || "Failed to send invoice email" });
      })
      .catch((error) => {
        console.error("Error sending invoice email:", error);
        showToast({ variant: "danger", message: "Error sending invoice email" });
      });
  };

  const handleSendInvoiceFollowupMail = (invoice) => {
    const invoiceId = invoice?.id;

    if (!invoiceId) {
      showToast({ variant: "warning", message: "Invoice id is missing" });
      return;
    }

    sendLitigationInvoiceFollowupEmail(invoiceId)
      .then((res) => {
        if (res?.success) {
          showToast({ variant: "success", message: `Follow-up email sent for invoice ${invoice?.invoice_number || invoiceId}` });
          return;
        }

        showToast({ variant: "danger", message: res?.data?.message || "Failed to send follow-up email" });
      })
      .catch((error) => {
        console.error("Error sending follow-up invoice email:", error);
        showToast({ variant: "danger", message: "Error sending follow-up email" });
      });
  };

  const handleGenerateLitigationInvoices = async (invoice) => {
    try {
      const resolvedClientId =
        invoice?.client_id ||
        (typeof invoice?.client === "number" || /^[0-9]+$/.test(String(invoice?.client || ""))
          ? invoice?.client
          : "");

      const res = await generateLitigationInvoices({
        client_id: resolvedClientId,
        id: invoice?.id,
      });
      if (res && res.success) {
        showToast({ variant: "success", message: `Invoices generated successfully for ${invoice?.invoice_number || invoice?.id || ""}` });
        handleRefetchData();
      } else {
        showToast({ variant: "danger", message: res?.data?.message || "Failed to generate invoices" });
      }
    } catch (error) {
      console.error("Error generating invoices:", error);
      showToast({ variant: "danger", message: "Error generating invoices" });
    }
  };

  const handleOpenUploadModal = async () => {
    setUploadOpen(true);
    setUploadClientsLoading(true);
    try {
      const res = await getClients({ limit: 100 });
      if (res && res.success && res.data) {
        const clientsList = res.data?.data?.results || res.data?.results || [];
        setUploadClients(Array.isArray(clientsList) ? clientsList : []);
      } else {
        setUploadClients([]);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setUploadClients([]);
      showToast({ variant: "danger", message: "Failed to fetch clients" });
    } finally {
      setUploadClientsLoading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setUploadOpen(false);
    setSelectedUploadClient("");
    setSelectedFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && isValidExcelFile(file)) {
      setSelectedFile(file);
    } else if (file) {
      showToast({ variant: "danger", message: "Only .xlsx and .xls files are allowed" });
    }
  };

  const isValidExcelFile = (file) => {
    const validTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    return validTypes.includes(file.type) || validExtensions.includes(fileExtension);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    if (file && isValidExcelFile(file)) {
      setSelectedFile(file);
    } else if (file) {
      showToast({ variant: "danger", message: "Only .xlsx and .xls files are allowed" });
    }
  };

  const handleUploadExcel = async () => {
    if (!selectedUploadClient || !selectedFile) {
      showToast({ variant: "warning", message: "Please select a client and file" });
      return;
    }

    setUploadLoading(true);
    try {
      const token = useAuthStore.getState().token;
      const formData = new FormData();
      formData.append("client_id", selectedUploadClient);
      formData.append("file", selectedFile);

      const response = await fetch(
        "https://api.themedius.ai/litigation/api/upload-invoice-excel/",
        {
          method: "POST",
          headers: {
            Authorization: `Token ${token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        showToast({ variant: "success", message: "Excel file uploaded successfully!" });
        handleCloseUploadModal();
        handleRefetchData();
      } else {
        const errorData = await response.json();
        showToast({ variant: "danger", message: errorData?.message || "Failed to upload file" });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      showToast({ variant: "danger", message: "Error uploading file" });
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white border-bottom px-3 py-3">
        <div className="row w-100 g-2 align-items-center">
          <div className="col-12 col-lg-6 d-flex align-items-center gap-2">
            <FilterButton
              onClick={() => setOpenFilterModal(true)}
              active={hasActiveFilters}
            />
          </div>

          <div className="col-12 col-lg-6 d-flex justify-content-lg-end gap-3">
            {selectedRows.length > 0 && (
              <Dropdown>
                <Dropdown.Toggle
                  variant="btn-primary"
                  className="btn btn-primary btn-lg px-4 py-2 fw-semibold"
                  disabled={bulkActionLoading}
                >
                  {bulkActionLoading ? "Processing..." : `Bulk Action (${selectedRows.length})`}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item onClick={() => handleBulkActionSelect("download")} disabled={bulkActionLoading}>
                    Download Invoices
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleBulkActionSelect("mark_paid")} disabled={bulkActionLoading}>
                    Mark as Paid
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleBulkActionSelect("send_email")} disabled={bulkActionLoading}>
                    Send Invoice via Email
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleBulkActionSelect("followup_email")} disabled={bulkActionLoading}>
                    Followup Email
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleBulkActionSelect("generate")} disabled={bulkActionLoading}>
                    Generate Invoice
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
            <button
              className="btn btn-primary btn-lg px-4 py-2 fw-semibold"
              onClick={handleAddInvoice}
              title="Add a new invoice"
            >
              Add Invoice
            </button>
            <button
              className="btn btn-primary btn-lg px-4 py-2 fw-semibold"
              onClick={handleOpenUploadModal}
              title="Upload Excel file to generate invoices"
            >
               Upload Excel
            </button>
          </div>
        </div>
      </div>

      <div className="p-3">
        {!!data?.length && !loading && (
          <>
            <div style={{ overflow: "scroll" }}>
              <table style={{ maxWidth: "100%", overflowX: "hidden" }} className="table table-bordered border table-sm">
                <thead className="thead-light">
                  <tr>
                    <th className="p-3 text-center align-top" style={{ width: "2.5rem" }}>
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={handleToggleSelectAllVisible}
                        aria-label="Select all visible invoices"
                      />
                    </th>
                    <th className="p-3 text-center align-top">#</th>
                    <th className="p-3 text-left align-top">Client</th>
                    <th className="p-3 text-left align-top">Branch Name</th>
                    <th className="p-3 text-left align-top">LAN</th>
                    <th className="p-3 text-left align-top">Customer Name</th>
                    <th className="p-3 text-left align-top">Invoice Date</th>
                    <th className="p-3 text-left align-top">Invoice Amount</th>
                    <th className="p-3 text-left align-top">Invoice Number</th>
                    <th className="p-3 text-left align-top">Remark</th>
                    <th className="p-3 text-center align-top">Is Paid</th>
                    <th className="p-3 text-center align-top">Status</th>
                    <th className="p-3 text-center align-top">Admin Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={item?.id || index} style={{ background: index % 2 === 0 ? "transparent" : "#fbfbfb" }}>
                      <td className="p-3 text-center" style={{ width: "2.5rem" }}>
                        <input
                          type="checkbox"
                          checked={selectedSet.has(item?.id)}
                          onChange={() => handleToggleRowSelection(item)}
                          aria-label={`Select invoice ${item?.invoice_number || item?.id || ""}`}
                        />
                      </td>
                      <td className="p-3 text-center" style={{ width: "1rem" }}>
                        {limitPerPage * (currentPage - 1) + index + 1}
                      </td>
                      <td className="p-3 text-left">
                        <div className="fw-semibold text-start text-break" style={{ cursor: 'default', color: 'inherit' }}>
                          {item?.client_name || item?.client || "-"}
                        </div>
                      </td>
                      <td className="p-3 text-left text-break">
                        {item?.branch_name || item?.branch || "-"}
                      </td>
                      <td className="p-3 text-left">
                        {item?.lan_number || item?.lan || "-"}
                      </td>
                      <td className="p-3 text-left">
                        {item?.customer_name || "-"}
                      </td>
                      <td className="p-3 text-left">
                        {formatInvoiceDate(item?.invoice_date)}
                      </td>
                      <td className="p-3 text-left">
                        {formatInvoiceAmount(item?.invoice_amount)}
                      </td>
                      <td className="p-3 text-left">
                        <strong>{item?.invoice_number || "-"}</strong>
                      </td>
                      <td className="p-3 text-left text-break">
                        {item?.additional_remark || "-"}
                      </td>
                      <td className="p-3 text-center">
                        {formatPaidStatus(item?.is_paid)}
                      </td>
                      <td className="p-3 text-center">
                        {item?.status || "-"}
                      </td>
                      <td className="p-3 text-center" style={{ minWidth: "12rem" }}>
                        <Dropdown>
                          <Dropdown.Toggle variant="primary" className="w-100 text-start py-2 select-style">
                            Action <img src={Arrow} alt="Sort" />
                          </Dropdown.Toggle>

                          <Dropdown.Menu className="select-style-dropdown">
                            <Dropdown.Item onClick={() => handleViewInvoice(item)}>
                              View Invoice
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleEditInvoice(item)}>
                              Edit Invoice
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleMarkAsPaid(item)}>
                              Mark as Paid
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleDownloadInvoice(item)}
                              disabled={!item?.pdf_url}
                            >
                              Download Invoice
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleViewPdfInvoice(item)}
                              disabled={!item?.pdf_url}
                            >
                              View PDF
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleSendInvoiceMail(item)}>
                              Send Invoice via Mail
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleSendInvoiceFollowupMail(item)}>
                              Followup Email
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleGenerateLitigationInvoices(item)}>
                              Generate Invoices
                            </Dropdown.Item>
                            
                          </Dropdown.Menu>
                        </Dropdown>

                      
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex flex-column align-items-center gap-2 mt-2">
              <p className="text-muted mb-0">Showing {data.length} records | Total records: {total}</p>
             
              <Pagination
                current={currentPage}
                total={total}
                onPageChange={handlePageChange}
                totalPages={paginationProps.pageTotal}
              />
            </div>
          </>
        )}

        {!data?.length && !loading && <NoRecord />}
        {loading && (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border text-primary" role="status" aria-hidden="true" />
          </div>
        )}
      </div>

      <InvoiceDetailsModal
        show={showInvoiceModal}
        mode={invoiceModalMode}
        invoice={selectedInvoice}
        formData={editFormData}
        clientOptions={invoiceClients}
        branchOptions={invoiceBranchOptions}
        clientsLoading={invoiceClientsLoading}
        branchesLoading={invoiceBranchesLoading}
        onClose={handleCloseInvoiceDetails}
        onEditRequest={handleInvoiceModalEditRequest}
        onSave={handleEditFormSubmit}
        onFieldChange={handleEditFormChange}
        saving={editLoading}
      />

      <InvoiceFilterModal
        show={openFilterModal}
        close={() => setOpenFilterModal(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={filter}
      />

      <Modal
        show={uploadOpen}
        onHide={handleCloseUploadModal}
        centered
        size="lg"
        contentClassName="border-0 shadow-lg rounded-4"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-5 fw-semibold">Upload Invoice Excel</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-2">
          <div className="container-fluid px-0">
            {/* Client Dropdown */}
            <div className="mb-4">
              <label className="form-label fw-semibold mb-2">Select Client *</label>
              <select
                className="form-select"
                value={selectedUploadClient}
                onChange={(e) => setSelectedUploadClient(e.target.value)}
                disabled={uploadClientsLoading || uploadLoading}
              >
                <option value="">Choose a client...</option>
                {uploadClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.full_name || client.name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div className="mb-4">
              <label className="form-label fw-semibold mb-2">Upload Excel File *</label>
              <div
                className={`border-2 border-dashed rounded-3 p-5 text-center cursor-pointer transition ${
                  dragActive ? "bg-primary bg-opacity-10 border-primary" : "bg-light border-secondary"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="excelInput"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={uploadLoading}
                  style={{ display: "none" }}
                />
                <label htmlFor="excelInput" className="cursor-pointer d-block">
                  <div className="fs-4 mb-2">📊</div>
                  <div className="fw-semibold mb-1">Drag and drop your Excel file here</div>
                  <div className="text-muted small">or click to browse</div>
                </label>
              </div>

              {selectedFile && (
                <div className="mt-3 alert alert-success d-flex align-items-center justify-content-between">
                  <div>
                    <strong>Selected file:</strong> {selectedFile.name}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setSelectedFile(null)}
                    disabled={uploadLoading}
                  >
                    Remove
                  </button>
                </div>
              )}

              <div className="text-muted small mt-2">
                Allowed formats: <strong>.xlsx, .xls</strong>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCloseUploadModal}
            disabled={uploadLoading}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUploadExcel}
            disabled={!selectedUploadClient || !selectedFile || uploadLoading}
          >
            {uploadLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBulkMarkPaidConfirm} onHide={() => setShowBulkMarkPaidConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Mark as Paid</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to mark {selectedRows.length} selected invoice(s) as paid?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowBulkMarkPaidConfirm(false)}
            disabled={bulkActionLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={executeBulkMarkAsPaid}
            disabled={bulkActionLoading}
          >
            {bulkActionLoading && bulkAction === "mark_paid" ? "Marking..." : "Confirm"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* PDF Viewer Modal */}
      <Modal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        size="xl"
        centered
        dialogClassName="pdf-viewer-modal-dialog"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-5 fw-semibold">
            View PDF - Invoice {pdfModalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "80vh" }}>
          {pdfLoading ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 gap-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0">Generating preview...</p>
            </div>
          ) : pdfModalUrl ? (
            <div style={{ width: "100%", height: "100%", overflow: "auto", WebkitOverflowScrolling: "touch" }}>
              <object
                data={pdfModalUrl}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{ border: "none", display: "block", minHeight: "100%" }}
              >
                <iframe
                  src={pdfModalUrl}
                  title={`Invoice ${pdfModalTitle}`}
                  width="100%"
                  height="100%"
                  style={{ border: "none", display: "block", minHeight: "100%" }}
                  scrolling="yes"
                />
              </object>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <p className="text-muted">No PDF preview available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <button
            type="button"
            className="btn btn-secondary btn-lg px-4 py-2"
            onClick={handleClosePdfModal}
          >
            Close
          </button>
          {pdfModalUrl && (
            <a
              href={pdfModalUrl}
              download={`Invoice_${pdfModalTitle}.pdf`}
              className="btn btn-primary btn-lg px-4 py-2"
            >
              Download PDF
            </a>
          )}
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default ViewInvoice;